import {InfiniteData, useQuery, useQueryClient} from "@tanstack/react-query";
import {CustomOutlet} from "components/Admin/CustomOutlet/CustomOutlet";
import {Popup} from "components/Admin/Popup/Popup";
import {useCustomNavigation} from "hooks/useCustomNavigation";
import {isArray, isEqual} from "lodash";
import React, {ReactNode, useMemo} from "react";
import {RecordContextProvider, Show, SimpleShowLayout, useNotify, useResourceContext, useTranslate} from "react-admin";
import {DialogActions, DialogContent, Skeleton} from "@mui/material";
import {useId} from "../../../../hooks/useId";
import {SecureButton} from "../../Secure/SecureButton/SecureButton";

export interface ViewPopupProps<TRecord> {
  queryKey?: string;
  label?: (record: TRecord) => string;
  loader?: ReactNode;
  buttons?: ReactNode;
  children: ((record: TRecord) => ReactNode) | ReactNode;
  query: (props: { id: any }) => Promise<TRecord>;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const ViewPopup: <TRecord extends { id: any }>(
  props: ViewPopupProps<TRecord>
) => JSX.Element = <TRecord extends { id: any }>(
  props: ViewPopupProps<TRecord>
) => {
  const t = useTranslate();

  const id = useId();

  const queryKey = useResourceContext({resource: props.queryKey});

  const resource = useResourceContext();

  if (!queryKey) {
    throw new Error("Query key or resource key is required");
  }

  const notify = useNotify();

  const {navigate, state} = useCustomNavigation({
    delay: 300,
  });

  const queryClient = useQueryClient();

  const listQueries = useMemo(() => {
    return queryClient
      .getQueryCache()
      .findAll()
      .filter(
        (query) =>
          isArray(query.queryKey) &&
          isEqual(query.queryKey.slice(0, 2), [queryKey, "list"])
      );
  }, [queryKey, queryClient]);

  const defaultValue = useMemo(() => {
    const value = queryClient.getQueryData<TRecord>([queryKey, id]);

    if (value) {
      return value;
    }

    for (const query of listQueries) {
      const result = queryClient
        .getQueryData<InfiniteData<TRecord[], unknown>>(query.queryKey)
        ?.pages?.flat()
        .find((item) => item.id === id);

      if (result) {
        return result;
      }
    }

    return undefined;
  }, [queryClient, queryKey, id, listQueries]);

  const query = useQuery({
    queryKey: [queryKey, id],
    queryFn: () => {
      return props.query({id: id}).catch((error) => {
        if (!error?.message) {
          return;
        }

        notify(error?.message, {type: "error"});
      });
    },
    initialData: defaultValue,
    // refetchOnMount: false,
    staleTime: 1000 * 60 * 5,
  });

  const label = useMemo(() => {
    if (!query.data) {
      return t("ra.message.loading");
    }

    if (props.label && query.data) {
      return props.label(query.data);
    }

    if (resource) {
      return t(`resources.${resource}.name`, {smart_count: 5, _: resource});
    }

    return "";
  }, [query.data, props, resource, t]);

  const close = () => {
    navigate("..");
  };

  const defaultLoader = useMemo(() => props.loader ?? (
    <>
      <DialogContent>
        <Skeleton sx={{height: 500, mb: 2}} variant="rounded"/>
      </DialogContent>
      <DialogActions>
        <Skeleton sx={{height: 30, width: 250}} variant="rounded"/>
      </DialogActions>
    </>
  ), [props.loader])

  const buttons = useMemo(() => {
    if (props.buttons) {
      return props.buttons
    }

    return (
      <>
        <SecureButton color="error" variant="contained" href="./delete">
          {t("ra.action.delete")}
        </SecureButton>
        <SecureButton color="primary" variant="contained" href="./edit">
          {t("ra.action.edit")}
        </SecureButton>
      </>
    )
  }, [props.buttons, t])

  const content = useMemo(() => {
    if (!query.data) {
      return defaultLoader;
    }

    if (typeof props.children == "function") {
      return props.children(query.data)
    }

    return (
      <RecordContextProvider value={query.data}>
        <SimpleShowLayout>
          {props.children}
        </SimpleShowLayout>
      </RecordContextProvider>
    );
  }, [defaultLoader, props, query.data])

  return (
    <>
      <CustomOutlet/>
      <Popup show={state === "show"} onClose={close} label={label} maxWidth={props.maxWidth}>
        <DialogContent>
          {content}
        </DialogContent>
        <DialogActions>
          {buttons}
        </DialogActions>
      </Popup>
    </>
  );
};
